import firebase from 'firebase/app';
import {
    NOTESLIST_INTEGRATION_LINK_URL,
    NOTESLIST_INTEGRATION_UNLINK_URL,
    NOTESLIST_INTEGRATION_STATUS_URL,
    NOTESLIST_ACCOUNT_STATUS_URL,
    NOTESLIST_ACCOUNT_START_URL,
    NOTESLIST_ACCOUNT_UNLINK_URL,
    NOTESLIST_PRODUCTIONS_URL,
    NOTESLIST_RESYNC_URL,
    NOTESLIST_NOTIFY_STRUCTURE_URL,
    NOTESLIST_UPGRADE_TO_DUAL_URL,
    NOTESLIST_PREVIEW_SWITCH_URL,
    NOTESLIST_APPLY_SWITCH_URL,
    NOTESLIST_CONFIRM_SWITCH_URL,
    NOTESLIST_SWITCH_CUELIST_INTERVAL_URL,
    NOTESLIST_SWITCH_TO_STUDENT_URL,
    NOTESLIST_DOWNGRADE_FROM_DUAL_URL,
    NOTESLIST_CANCEL_PENDING_DOWNGRADE_URL,
    NOTESLIST_SUBSCRIPTION_PERIOD_END_URL,
    NOTESLIST_AFFILIATE_SEATS_SYNC_URL,
    NOTESLIST_ROSTER_SYNC_URL,
    NOTESLIST_INTEGRATION_ENABLED
} from '@/lib/URLs';
import NOTESLIST_STRUCTURE_EVENTS from '@/lib/integration-events';
import { createDebouncedNotifyQueue } from '@/lib/noteslist-notify-queue';

const DEBOUNCE_MS = 2000;

let notifyQueue = null;

async function getIdToken() {
    const user = firebase.auth().currentUser;
    if (!user) {
        throw new Error('You must be signed in to manage Notes List linking');
    }
    return user.getIdToken();
}

async function integrationRequest(url, options = {}) {
    const token = await getIdToken();
    let response;
    try {
        response = await fetch(url, {
            method: options.method || 'GET',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`
            },
            body: options.body ? JSON.stringify(options.body) : undefined
        });
    } catch (networkErr) {
        const error = new Error(
            (networkErr && networkErr.message) ||
                'Notes List integration request failed (network)'
        );
        error.cause = networkErr;
        error.url = url;
        console.error('[noteslist-integration] fetch failed', url, networkErr);
        throw error;
    }
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
        const error = new Error(data.error || 'Notes List integration request failed');
        error.status = response.status;
        error.code = data.code;
        error.url = url;
        throw error;
    }
    return data;
}

async function emitStructureBatchWithFullFallback(projectId, batch) {
    try {
        const single = batch.length === 1 ? batch[0] : null;
        const body = single && single.event === NOTESLIST_STRUCTURE_EVENTS.STRUCTURE_FULL
            ? {
                projectId,
                event: NOTESLIST_STRUCTURE_EVENTS.STRUCTURE_FULL
            }
            : {
                projectId,
                event: NOTESLIST_STRUCTURE_EVENTS.STRUCTURE_BATCH,
                changes: batch
            };
        await integrationRequest(NOTESLIST_NOTIFY_STRUCTURE_URL, {
            method: 'POST',
            body
        });
    } catch (err) {
        console.warn(
            '[noteslist-integration] batched structure notify failed; falling back to structure.full:',
            err.message
        );
        await integrationRequest(NOTESLIST_NOTIFY_STRUCTURE_URL, {
            method: 'POST',
            body: {
                projectId,
                event: NOTESLIST_STRUCTURE_EVENTS.STRUCTURE_FULL
            }
        });
    }
}

function getNotifyQueue(projectId) {
    if (!notifyQueue || notifyQueue.projectId !== projectId) {
        notifyQueue = {
            projectId,
            queue: createDebouncedNotifyQueue({
                debounceMs: DEBOUNCE_MS,
                onFlush: {
                    structureFullEvent: NOTESLIST_STRUCTURE_EVENTS.STRUCTURE_FULL,
                    flush: async (batch) => {
                        try {
                            await emitStructureBatchWithFullFallback(projectId, batch);
                        } catch (err) {
                            console.warn('[noteslist-integration] structure notify failed:', err.message);
                        }
                    }
                }
            })
        };
    }
    return notifyQueue.queue;
}

export default {
    async fetchNotesListAccountStatus() {
        return integrationRequest(NOTESLIST_ACCOUNT_STATUS_URL, { method: 'GET' });
    },
    async startNotesListAccountLink() {
        return integrationRequest(NOTESLIST_ACCOUNT_START_URL, { method: 'POST', body: {} });
    },
    async unlinkNotesListAccount() {
        return integrationRequest(NOTESLIST_ACCOUNT_UNLINK_URL, { method: 'POST', body: {} });
    },
    async fetchLinkableNotesListProductions() {
        return integrationRequest(NOTESLIST_PRODUCTIONS_URL, { method: 'GET' });
    },
    async fetchNotesListLinkStatus(_, { projectId }) {
        const url = `${NOTESLIST_INTEGRATION_STATUS_URL}?projectId=${encodeURIComponent(projectId)}`;
        return integrationRequest(url, { method: 'GET' });
    },
    async linkNotesListProject(_, payload) {
        return integrationRequest(NOTESLIST_INTEGRATION_LINK_URL, {
            method: 'POST',
            body: {
                projectId: payload.projectId,
                mode: payload.mode || 'create',
                productionId: payload.productionId || null
            }
        });
    },
    async unlinkNotesListProject(_, { projectId }) {
        return integrationRequest(NOTESLIST_INTEGRATION_UNLINK_URL, {
            method: 'POST',
            body: { projectId }
        });
    },
    async resyncNotesListProject(_, { projectId }) {
        return integrationRequest(NOTESLIST_RESYNC_URL, {
            method: 'POST',
            body: { projectId }
        });
    },
    async upgradeNotesListToDual(_, { interval }) {
        return integrationRequest(NOTESLIST_UPGRADE_TO_DUAL_URL, {
            method: 'POST',
            body: {
                interval: interval === 'year' ? 'year' : 'month'
            }
        });
    },
    async previewNotesListSwitch(_, { target, interval }) {
        return integrationRequest(NOTESLIST_PREVIEW_SWITCH_URL, {
            method: 'POST',
            body: {
                target,
                interval: interval === 'year' ? 'year' : 'month'
            }
        });
    },
    async fetchNotesListSubscriptionPeriodEnd() {
        return integrationRequest(NOTESLIST_SUBSCRIPTION_PERIOD_END_URL, {
            method: 'POST',
            body: {}
        });
    },
    async applyNotesListPlanSwitch(_, { target }) {
        return integrationRequest(NOTESLIST_APPLY_SWITCH_URL, {
            method: 'POST',
            body: { target }
        });
    },
    async confirmNotesListSwitch() {
        return integrationRequest(NOTESLIST_CONFIRM_SWITCH_URL, {
            method: 'POST',
            body: {}
        });
    },
    async switchCuelistIndividualInterval(_, { interval }) {
        return integrationRequest(NOTESLIST_SWITCH_CUELIST_INTERVAL_URL, {
            method: 'POST',
            body: {
                interval: interval === 'year' ? 'year' : 'month'
            }
        });
    },
    // A full-price Cuelist Individual subscriber moves to the student price (functions switchToStudentRate).
    async switchToStudentRate(_, { interval }) {
        return integrationRequest(NOTESLIST_SWITCH_TO_STUDENT_URL, {
            method: 'POST',
            body: {
                interval: interval === 'year' ? 'year' : 'month'
            }
        });
    },
    async downgradeNotesListFromDual(_, { interval }) {
        return integrationRequest(NOTESLIST_DOWNGRADE_FROM_DUAL_URL, {
            method: 'POST',
            body: {
                interval: interval === 'year' ? 'year' : 'month'
            }
        });
    },
    async cancelNotesListPendingDowngrade() {
        return integrationRequest(NOTESLIST_CANCEL_PENDING_DOWNGRADE_URL, {
            method: 'POST',
            body: {}
        });
    },
    /**
     * After Manage Active Users Confirm Changes: push Dual seat_granted /
     * seat_revoked via HTTP (works when Firestore triggers are ignored under
     * `firebase emulators:start --only functions`).
     */
    async syncNotesListAffiliateSeats(_, { previousAffiliateUsers, affiliateUsers }) {
        return integrationRequest(NOTESLIST_AFFILIATE_SEATS_SYNC_URL, {
            method: 'POST',
            body: {
                previousAffiliateUsers: previousAffiliateUsers || {},
                affiliateUsers: affiliateUsers || {}
            }
        });
    },
    /**
     * After project collaborator add/remove/role/ownership change: push
     * collaborator_added / collaborator_removed + roster.full via HTTP when
     * Firestore triggers are ignored under `--only functions`.
     */
    async syncNotesListProjectRoster(
        _,
        { projectId, previousCollaborators, previousOwnerUID, addedUids, removedUids }
    ) {
        const result = await integrationRequest(NOTESLIST_ROSTER_SYNC_URL, {
            method: 'POST',
            body: {
                projectId,
                previousCollaborators:
                    previousCollaborators != null ? previousCollaborators : null,
                previousOwnerUID:
                    previousOwnerUID !== undefined ? previousOwnerUID : null,
                addedUids: Array.isArray(addedUids) ? addedUids : undefined,
                removedUids: Array.isArray(removedUids) ? removedUids : undefined
            }
        });
        if (result && result.skipped) {
            console.warn(
                '[noteslist-integration] roster/sync skipped',
                projectId,
                result.reason || result
            );
        }
        return result;
    },
    queueNotesListStructureEvent({ getters }, eventPayload) {
        // Bail when the integration is off for this ENVIRONMENT, not just when the
        // project is unlinked. A project document can carry link_status 'linked'
        // in an environment that has no partner credentials — sandbox has exactly
        // that, seeded for UI work — and without this the every-cue-write path
        // POSTs to a gate that answers 403. The call sites do not catch it: two
        // dispatch without awaiting (unhandled rejection) and updateCue returns
        // the chain, so the rejection reaches its caller and a cue edit fails.
        if (!NOTESLIST_INTEGRATION_ENABLED) {
            return Promise.resolve();
        }
        const project = getters.project;
        if (!project || project.noteslist_link_status !== 'linked') {
            return Promise.resolve();
        }
        return getNotifyQueue(project.id).enqueue(eventPayload);
    },
    notifyNotesListStructureIfLinked({ dispatch }) {
        return dispatch('queueNotesListStructureEvent', {
            event: NOTESLIST_STRUCTURE_EVENTS.STRUCTURE_FULL
        });
    }
};


// WEBPACK FOOTER //
// ./src/lib/vuex/integration-actions.js